<template>
	<li
		v-if="!isShippingLineItem"
		class="relative"
		:class="{
			'relative py-6 border-b border-b-gray-lighter flex': true
		}"
	>
		<div class="shrink-0 overflow-hidden bg-white mr-4 md:mr-6">
			<nuxt-img
				:src="itemImage"
				:alt="itemRef?.Name || ''"
				sizes="120px"
				class="h-[96px] w-[96px] object-contain object-center md:h-[120px] md:w-[120px]"
				:provider="itemRef.ImageKey || imageUrl ? undefined : 'storyblok'"
			/>
		</div>
		<div>
			<div>
				<nuxt-link
					:to="routeToProduct"
					class="text-md text-secondary font-medium mb-2 block"
				>
					{{ itemRef.Name }}
				</nuxt-link>
			</div>
			<div class="gap-2 text-sm mb-4 flex">
				<Price
					:old-price="oldPrice"
					:price="itemRef.PriceDisplayIncVat!"
					:show-save-text="true"
				/>
			</div>

			<p v-if="deliveryText" class="text-sm text-gray-super-dark mb-4">
				{{ deliveryText }}
			</p>

			<template v-if="itemType === 'product-minicart'">
				<QuantitySelector
					v-model="quantity"
					class="!h-[38px] !w-[108px] mt-4"
					:min="1"
					:max="maxQuantity"
					:disabled="getIsLoading"
					@update:modelValue="updateItem()"
				/>
			</template>
		</div>
		<TrashIcon
			v-if="itemType === 'product-minicart'"
			class="shrink-0 cursor-pointer text-rimary ml-auto h-6 w-6"
			@click="removeItemFromCart"
		/>
	</li>
</template>

<script setup lang="ts">
import { TrashIcon } from "@heroicons/vue/24/outline";
import type { BasketItem } from "@nwt/norce-pwa/dist/runtime/types/api/shopping/basket";
import Price from "../Product/ProductAttributes/Price.vue";
import QuantitySelector from "../Product/ProductAttributes/AddToCart/QuantitySelector.vue";
import { isShippingItem } from "~/utils/cart/isShippingItem";

interface Props {
	lineItem: Partial<BasketItem>;
	itemType: string;
	imageUrl?: string;
}

const { lineItem, itemType, imageUrl } = defineProps<Props>();

const config = useRuntimeConfig();
const cartStore = useCartStore();
const { getIsLoading } = storeToRefs(useHelpersStore());
const localePath = useLocalePath();

const ImagesCdnUrl = config.public.norce.cdn;
const itemRef = toRef(() => lineItem);
const isShippingLineItem = computed(() => isShippingItem(itemRef.value));

const productStore = useProductStore();
const { removeFromCart } = useGtmEvents();
const { getDeliveryText } = useDeliveryText();

const deliveryText = computed(() => getDeliveryText(itemRef.value, "cart"));
const { oldPrice } = useProductPrices(itemRef.value);
const itemImage = computed(() => {
	if (imageUrl) {
		return imageUrl;
	} else
		return imageUrl
			? `${ImagesCdnUrl}/${imageUrl}?format=webp&w=120`
			: lineItem?.ImageKey
				? `${ImagesCdnUrl}/${lineItem.ImageKey}?format=webp&w=120`
				: productStore.defaultProductImage;
});
const routeToProduct = computed(() =>
	localePath(`/${itemRef.value?.UniqueName}`)
);

const quantity = ref<number>(itemRef.value?.Quantity ?? 0);

// Stock comes with the basket line itself (`OnHandValue`, mirrored by
// `OnHand.Value`), so the quantity cap needs no network call. This used to
// fetch the whole product per line, on mount — `get-product-by-unique-name`
// is the one product endpoint without a Redis cache, so a five-line cart cost
// five uncached Norce calls every time the cart rendered. Ported from
// `layers/base/components/Shared/SharedProductOrderItem.vue`.
const maxQuantity = computed(
	() => itemRef.value.OnHandValue ?? itemRef.value.OnHand?.Value ?? 0
);

const updateItem = async () => {
	cartStore?.updateCartItem(
		itemRef.value.LineNo!,
		itemRef.value.PartNo!,
		quantity.value!
	);
};

const removeItemFromCart = () => {
	// GA4 remove_from_cart, reported before the line is gone so the event still
	// carries it. This fork predates the base wiring and had lost the event.
	removeFromCart(itemRef.value);
	cartStore?.removeFromCart(itemRef.value.LineNo!);
};

watch(itemRef, () => {
	quantity.value = itemRef.value.Quantity ?? 0;
});
</script>
